import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import styles from "./ConsentTemplatesPage.module.scss";
import Header from "../Header/header";
import SetupSteps, { Step } from "../Register/SetupSteps/SetupSteps";
import { FiEdit2 } from "react-icons/fi";
import { toast } from "react-toastify";
import ImageGalleryV2 from "../../ImageGallery/imageGalleryV3";

const STEPS: Step[] = [
  { key: "practice", label: "Practice and personal info", path: "/practiceDetails" },
  { key: "logo", label: "Logo Management", path: "/logoManagement" },
  { key: "social", label: "Social Media Links", path: "/socialLinks" },
  { key: "portfolio", label: "Portfolio Gallery", path: "/portfolioGallery" },
  { key: "consent", label: "Consent Templates", path: "/consentTemplates" },
  { key: "preview", label: "Preview & Complete", path: "/previewComplete" },
];

type Template = {
  id: number;
  name: string;
  body: string;
  level?: string;
  created_at?: string;
};

type Treatment = {
  id: number;
  category_type: string;
  category_name: string;
};

declare global {
  interface Window {
    CKEDITOR: any;
  }
}

export default function ConsentTemplatesPage() {
  const navigate = useNavigate();
  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  const [mode, setMode] = useState<"empty" | "builder">("empty");
  const [tab, setTab] = useState<"existing" | "generate">("existing");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [practiceTemplates, setPracticeTemplates] = useState<Template[]>([]);
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<Template | null>(null);
  const [editTemplate, setEditTemplate] = useState<Template | null>(null);
  const [user, setUser] = useState<any>(null);
  const [selectedLevel, setSelectedLevel] = useState<"user" | "practice">("user");
  const [selectedTreatments, setSelectedTreatments] = useState<number[]>([]);
  const [treatmentSearch, setTreatmentSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
const [deleting, setDeleting] = useState(false);

const [showGallery, setShowGallery] = useState(false);
const [activeEditorId, setActiveEditorId] = useState("editor1");

useEffect(() => {
  const handler = (e: any) => {
    const editorId = e?.detail?.editorId || "editor1";
    setActiveEditorId(editorId);
    setShowGallery(true);
  };

  window.addEventListener("ckeditor-open-gallery", handler as EventListener);
  return () => window.removeEventListener("ckeditor-open-gallery", handler as EventListener);
}, []);

useEffect(() => {
  const handler = (e: any) => {
    const id = e?.detail?.editorId || "editor1";
    setActiveEditorId(id);
    setGalleryOpen(true);
  };

  window.addEventListener("ckeditor-open-gallery", handler as EventListener);
  return () => window.removeEventListener("ckeditor-open-gallery", handler as EventListener);
}, []);

  /* -------------------- Upload inline images -------------------- */
  const uploadCallback = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await axios.post("/uploads/image", formData);
    return res;
  };

  /* -------------------- Fetch user -------------------- */
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await axios.get("/user");
        if (Array.isArray(data) && data.length > 1) setUser(data[1]);
        else setUser(data);
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    fetchUser();
  }, []);

  /* -------------------- Fetch treatments -------------------- */
  useEffect(() => {
  const fetchTreatments = async () => {
    try {
      const token =
        axios.defaults.headers.common.Authorization?.replace("Bearer ", "") ||
        localStorage.getItem("token") ||
        Cookies.get("token");

      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}treatment`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTreatments(res.data?.treatments || []);
    } catch (err) {
      console.error("Failed to load treatments:", err);
      setTreatments([]); // safe fallback
    }
  };
  fetchTreatments();
}, []);

  /* -------------------- Fetch templates -------------------- */
  useEffect(() => {
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const token =
        axios.defaults.headers.common.Authorization?.replace("Bearer ", "") ||
        localStorage.getItem("token") ||
        Cookies.get("token");

      const endpoint =
        user?.level === "practice"
          ? `${process.env.REACT_APP_BASE_URL_V2}onboarding/get_practice_consent_templates`
          : `${process.env.REACT_APP_BASE_URL_V2}onboarding/get_practitioner_consent_templates`;

      const res = await axios.get(endpoint, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      setPracticeTemplates(res.data || []);
    } catch (err) {
      console.error("Failed to load templates:", err);
    } finally {
      setLoading(false);
    }
  };

  // Run only when user is loaded
  if (user?.level) {
    fetchTemplates();
  }
}, [user?.level]);

  useEffect(() => {
    const fetchGlobalTemplates = async () => {
      if (tab !== "existing" || mode !== "builder") return;
      try {
        setLoading(true);
        const res = await axios.get(
         `${process.env.REACT_APP_BASE_URL_V2}onboarding/get_global_consent_templates`,
          { headers: { Accept: "application/json" } }
        );
        setTemplates(res.data || []);
      } catch (err) {
        console.error("Failed to load global templates:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchGlobalTemplates();
  }, [tab, mode]);

  /* -------------------- CKEditor setup -------------------- */
useEffect(() => {
  if (!editTemplate) return;

  const destroyAllEditors = () => {
    const instances = window.CKEDITOR?.instances || {};
    Object.keys(instances).forEach((key) => {
      try {
        instances[key].destroy(true);
      } catch {}
    });
  };

  const loadEditor = () => {
    if (!editorRef.current) return;

    // Force stable id
    editorRef.current.id = "editor1";

    destroyAllEditors();

    const editor = window.CKEDITOR.replace("editor1", {
      height: 450,

      extraPlugins:
        "placeholder_select,simpleImageUpload,image2,image,signaturebuttons,gallerybutton,uploadimage",

      toolbar: [
        { name: "basicstyles", items: ["Bold", "Italic", "Underline", "Strike"] },
        {
          name: "paragraph",
          items: ["NumberedList", "BulletedList", "Outdent", "Indent", "Blockquote"],
        },
        { name: "links", items: ["Link", "Unlink"] },
        { name: "styles", items: ["Format", "Font", "FontSize"] },
        { name: "colors", items: ["TextColor", "BGColor"] },
        {
          name: "insert",
          items: ["GalleryButton", "SignatureMandatory", "SignatureOptional", "ClinicianSignature"],
        },
      ],

      allowedContent: true,
      image2_disableResizer: false,
      image_prefillDimensions: true,
    });

    editor.on("instanceReady", () => {
      editor.setData(editTemplate.body || "");
    });

    // keep activeEditorId aligned
    setActiveEditorId(editor.name || "editor1");
  };

  if (!window.CKEDITOR) {
    const script = document.createElement("script");
    script.src = "/ckeditor/ckeditor.js";
    script.onload = loadEditor;
    document.body.appendChild(script);
  } else {
    loadEditor();
  }

  return () => {
    destroyAllEditors();
  };
}, [editTemplate?.id]);

const fetchPracticeTemplates = async () => {
  setLoading(true);
  try {
    const token =
      axios.defaults.headers.common.Authorization?.replace("Bearer ", "") ||
      localStorage.getItem("token") ||
      Cookies.get("token");

    const endpoint =
      user?.level === "practice"
        ? `${process.env.REACT_APP_BASE_URL_V2}onboarding/get_practice_consent_templates`
        : `${process.env.REACT_APP_BASE_URL_V2}onboarding/get_practitioner_consent_templates`;

    const res = await axios.get(endpoint, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    setPracticeTemplates(res.data || []);
  } catch (err) {
    console.error("Failed to load templates:", err);
  } finally {
    setLoading(false);
  }
};


  /* -------------------- Save Template -------------------- */
  const handleSave = async () => {
  if (!editTemplate) return;

  if (selectedTreatments.length === 0) {
    toast.error("Please select at least one treatment.");
    return;
  }

  try {
    const editorContent = window.CKEDITOR.instances.editor1.getData();
    let updatedContent = editorContent;

    // Upload inline images
    const base64Regex = /<img[^>]+src="data:image\/[^;]+;base64,[^"]+"[^>]*>/g;
    const base64Images = editorContent.match(base64Regex);

    if (base64Images) {
      for (const img of base64Images) {
        const base64Data =
          img.match(/src="(data:image\/[^;]+;base64,[^"]+)"/)?.[1];

        if (base64Data) {
          const blob = await fetch(base64Data).then((res) => res.blob());
          const file = new File([blob], "upload.png", { type: blob.type });
          const uploadRes = await uploadCallback(file);
          updatedContent = updatedContent.replace(base64Data, uploadRes.data);
        }
      }
    }

    const isEditingPracticeTemplate =
      editTemplate.level === "practice" &&
      practiceTemplates.some((t) => t.id === editTemplate.id);

    const payload = {
      name: editTemplate.name,
      body: updatedContent,
      treatment_ids: selectedTreatments,
    };

    if (isEditingPracticeTemplate) {
          const payload = {
            name: editTemplate.name,
            body: updatedContent,

            // REQUIRED by backend (match existing record)
            level: editTemplate.level,              // "practice" | "user"
            is_default: editTemplate.is_default ?? false,
            is_cloned: editTemplate.is_cloned ?? false,
            parent_id: editTemplate.parent_id ?? null,

            // Treatments
            treatment_id: selectedTreatments[0],    // backend still expects single
            treatment_ids: selectedTreatments,

            // Optional but present in API response
            category_id: editTemplate.category_id ?? null,
          };

          await axios.put(
            `${process.env.REACT_APP_BASE_URL}consent_clause/${editTemplate.id}`,
            payload,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );
        } else {
      // CLONE from global template
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}consent_clause/${editTemplate.id}/copy_global_template_to_private`,
        {
          ...payload,
          level: selectedLevel,
          is_cloned: false,
          is_default: false,
          parent_id: null,
          treatment_id: selectedTreatments[0],
        },
        { headers: { "Content-Type": "application/json" } }
      );
    }

    toast.success("Template saved successfully");
    setEditTemplate(null);
    setMode("empty");
    await fetchPracticeTemplates();
  } catch (err) {
    console.error(err);
    toast.error("Failed to save template");
  }
};


  const handleNext = () => navigate("/previewComplete");
  const handleBack = () => navigate("/portfolioGallery");

const handlePreviewFromEditor = () => {
  if (!editTemplate) return;

  const editorContent =
    window.CKEDITOR?.instances?.editor1?.getData() || editTemplate.body;

  setPreview({
    ...editTemplate,
    body: editorContent,
  });
};

const filteredTreatments = treatments.filter(
  (t) =>
    t.description
      ?.toLowerCase()
      .includes(treatmentSearch.toLowerCase()) &&
    !selectedTreatments.includes(t.id)
);

const handleEditPracticeTemplate = (template: Template & any) => {
  setEditTemplate({
    id: template.id,
    name: template.name,
    body: template.body,
    level: template.level,
  });

  setSelectedLevel(template.level);
  setSelectedTreatments(template.treatment_ids || []);
  setMode("builder");
};

const confirmDeleteTemplate = async () => {
  if (!deleteTarget) return;

  try {
    setDeleting(true);

    const token =
      axios.defaults.headers.common.Authorization?.replace("Bearer ", "") ||
      localStorage.getItem("token") ||
      Cookies.get("token");

    await axios.delete(
      `https://dev.api.medicube.health/api/v1/consent_clause/${deleteTarget.id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success("Template deleted successfully");
    setDeleteTarget(null);
    await fetchPracticeTemplates();
  } catch (err) {
    console.error(err);
    toast.error("Failed to delete template");
  } finally {
    setDeleting(false);
  }
};


  /* -------------------- Render -------------------- */
  return (
    <div className={styles.page}>
      <Header />
      <main className={styles.main}>
        <SetupSteps
          steps={STEPS}
          current={5}
          rightLabel="80% Complete"
          onStepClick={(step) => navigate(step.path)}
        />

        {mode === "empty" ? (
          <>
            <h1 className={styles.title}>Consent Templates</h1>
            <p className={styles.subtitle}>
              Create consent templates for your most common procedures.
            </p>

            <section className={styles.card}>
              <div className={styles.cardHeader}>
                <h2>Your Templates ({practiceTemplates.length})</h2>
                <button
                  className={styles.createBtnSmall}
                  onClick={() => setMode("builder")}
                >
                  + Create New Template
                </button>
              </div>

              {loading ? (
                <div className={styles.loading}>Loading...</div>
              ) : (
                <div className={styles.practiceTemplatesList}>
                  {practiceTemplates.map((t) => (
                    <div key={t.id} className={styles.practiceTemplateItem}>
                      <div>{t.name}</div>

                      <div className={styles.actions}>
                        <button
                          className={styles.viewBtn}
                          onClick={() => setPreview(t)}
                        >
                          View
                        </button>

                        <button
                            className={styles.ediBtn}
                          onClick={() => handleEditPracticeTemplate(t)}
                        >
                          Edit
                        </button>

                     <button
                          className={styles.delBtn}
                        onClick={() => setDeleteTarget(t)}
                      >
                        Delete
                      </button>

                      </div>
                    </div>
                  ))}
                </div>

              )}
            </section>
          </>
        ) : (
          <>
            <div className={styles.builderHeader}>
              <h1>Create Consent Template</h1>
              <button
                className={styles.backToListBtn}
                onClick={() => {
                  setMode("empty");
                  setTab("existing");
                }}
              >
                ← Back
              </button>
            </div>

            <section className={styles.card}>
              <div className={styles.templatesGrid}>
                {templates.map((t) => (
                  <div
                    key={t.id}
                    className={styles.templateItem}
                    onClick={() => {
                      setEditTemplate(t);
                      setSelectedTreatments([]);
                      setSelectedLevel("user");
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setEditTemplate(t);
                        setSelectedTreatments([]);
                        setSelectedLevel("user");
                      }
                    }}
                  >
                    <div className={styles.templateName}>{t.name}</div>
                    <FiEdit2 className={styles.editIcon} />
                  </div>
                ))}
              </div>
            </section>

          </>
        )}

        {deleteTarget && (
          <div
            className={styles.modalOverlay}
            onClick={() => setDeleteTarget(null)}
          >
            <div
              className={styles.confirmModal}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h3>Delete Template</h3>
                <button
                  className={styles.closeBtn}
                  onClick={() => setDeleteTarget(null)}
                >
                  ✕
                </button>
              </div>

              <div className={styles.modalBody}>
                <p>
                  Are you sure you want to delete
                  <strong> “{deleteTarget.name}”</strong>?
                </p>
                <p className={styles.warningText}>
                  This action cannot be undone.
                </p>
              </div>

              <div className={styles.modalFooter}>
                <button
                  className={styles.cancelBtn}
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                >
                  Cancel
                </button>

                <button
                  className={styles.deleteConfirmBtn}
                  onClick={confirmDeleteTemplate}
                  disabled={deleting}
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={styles.nextWrapper}>
          <button className={styles.backBtn} onClick={handleBack}>
            ← Back
          </button>
          <button className={styles.nextBtn} onClick={handleNext}>
            Next →
          </button>
        </div>
      </main>


      {/* ---------- Edit Modal ---------- */}
      {editTemplate && (
        <div className={styles.modalOverlay} onClick={() => setEditTemplate(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Edit Template</h2>
              <button className={styles.closeBtn} onClick={() => setEditTemplate(null)}>
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Name</label>
                <input
                  type="text"
                  value={editTemplate.name}
                  onChange={(e) =>
                    setEditTemplate({ ...editTemplate, name: e.target.value })
                  }
                />
              </div>

              <div className={styles.formGroup}>
                <label>Select Treatments</label>
                <div className={styles.multiSelectBox}>
                  <div className={styles.selectedChips}>
                    {selectedTreatments.map((id) => {
                      const treatment = treatments.find((t) => t.id === id);

                      return (
                        <div key={id} className={styles.chip}>
                          {treatment?.description}
                          <span
                            className={styles.removeChip}
                            onClick={() =>
                              setSelectedTreatments((prev) => prev.filter((x) => x !== id))
                            }
                          >
                            ✕
                          </span>
                        </div>
                      );
                    })}

                    <div className={styles.searchSelectWrapper}>
                    <input
                      type="text"
                      placeholder="Search treatments..."
                      className={styles.treatmentSearchInput}
                      value={treatmentSearch}
                      onChange={(e) => {
                        setTreatmentSearch(e.target.value);
                        setDropdownOpen(true);
                      }}
                      onFocus={() => setDropdownOpen(true)}
                    />

                    {dropdownOpen && filteredTreatments.length > 0 && (
                      <div className={styles.dropdownList}>
                        {filteredTreatments.map((t) => (
                          <div
                            key={t.id}
                            className={styles.dropdownItem}
                            onClick={() => {
                              setSelectedTreatments((prev) => [...prev, t.id]);
                              setTreatmentSearch("");
                              setDropdownOpen(false);
                            }}
                          >
                            {t.description}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  </div>
                </div>
              </div>

             {/* Automatically set level based on logged-in user */}
            {(() => {
              if (user?.level === "practice" && selectedLevel !== "practice") {
                setSelectedLevel("practice");
              } else if (user?.level === "user" && selectedLevel !== "user") {
                setSelectedLevel("user");
              }
              return null; 
            })()}


              <textarea
                id="editor1"
                ref={editorRef}
                className={styles.editorTextarea}
                defaultValue={editTemplate.body}
              />
            </div>

          <div className={styles.modalFooter}>
              <button
                className={styles.viewBtnOutlined}
                onClick={handlePreviewFromEditor}
              >
                Preview
              </button>

              <button className={styles.saveBtn} onClick={handleSave}>
                Save Template
              </button>
            </div>

          </div>
        </div>
      )}


      {showGallery && (
        <div className={styles.galleryOverlay} onClick={() => setShowGallery(false)}>
          <div className={styles.galleryPanel} onClick={(e) => e.stopPropagation()}>
            <ImageGalleryV2
              showGallery={showGallery}
              onClose={() => setShowGallery(false)}
              onSelectImage={(url: string) => {
                const editor = window.CKEDITOR?.instances?.[activeEditorId || "editor1"];
                if (!editor) return;
                editor.insertHtml(`<img src="${url}" alt="" />`);
                setShowGallery(false);
              }}
            />
          </div>
        </div>
      )}


      {preview && (
          <div className={styles.modalOverlay} onClick={() => setPreview(null)}>
            <div
              className={`${styles.modalContent} ${styles.previewModal}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h2>Preview: {preview.name}</h2>
                <button className={styles.closeBtn} onClick={() => setPreview(null)}>
                  ✕
                </button>
              </div>

              <div className={styles.modalBody}>
                <div
                  className={styles.previewBody}
                  dangerouslySetInnerHTML={{ __html: preview.body }}
                />
              </div>

              <div className={styles.modalFooter}>
                <button
                  className={styles.closeBtnOutlined}
                  onClick={() => setPreview(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
